/**
 * ImageCard - Individual image card with upload progress and actions
 * Supports both uploaded images and images being uploaded
 */

import React, { useState } from 'react';
import {
  Box,
  Card,
  CardMedia,
  IconButton,
  CircularProgress,
  Typography,
  Tooltip,
  alpha,
  useTheme,
  Fade,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import BlockIcon from '@mui/icons-material/Block';
import { ImageCardProps } from './types';

const ImageCard: React.FC<ImageCardProps> = ({
  image,
  uploadingImage,
  aspectRatio = 1,
  showDragHandle = false,
  onPreview,
  onDelete,
  disabled = false,
}) => {
  const theme = useTheme();
  const [isHovered, setIsHovered] = useState(false);

  const isUploading = !!uploadingImage;
  const src = isUploading ? uploadingImage.previewUrl : image?.url;
  const status = uploadingImage?.status;
  const progress = uploadingImage?.progress || 0;
  const hasError = status === 'error';
  const isCompleted = status === 'completed';
  const isPending = image?.status === 'pending';
  const isRejected = image?.status === 'rejected';

  // Determine if we should show the overlay
  const showOverlay = isHovered && !isUploading && !disabled;
  const showProgressOverlay = isUploading && !isCompleted;

  return (
    <Card
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        width: 160,
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 2,
        boxShadow: isHovered ? theme.shadows[4] : theme.shadows[1],
        transition: 'box-shadow 0.2s ease-in-out',
        border: hasError
          ? `2px solid ${theme.palette.error.main}`
          : isRejected
          ? `2px solid ${theme.palette.error.main}`
          : isPending
          ? `2px solid ${theme.palette.warning.main}`
          : 'none',
      }}
    >
      {/* Image */}
      <CardMedia
        component="img"
        image={src}
        alt="Station image"
        sx={{
          aspectRatio,
          objectFit: 'cover',
          filter: showProgressOverlay ? 'brightness(0.6)' : 'none',
          transition: 'filter 0.2s',
        }}
      />

      {/* Upload Progress Overlay */}
      {showProgressOverlay && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha(theme.palette.common.black, 0.5),
          }}
        >
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <CircularProgress
              variant="determinate"
              value={progress}
              size={56}
              thickness={4}
              sx={{
                color: theme.palette.common.white,
                '& .MuiCircularProgress-circle': {
                  strokeLinecap: 'round',
                },
              }}
            />
            <Box
              sx={{
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography
                variant="caption"
                component="div"
                sx={{ color: 'white', fontWeight: 600 }}
              >
                {`${Math.round(progress)}%`}
              </Typography>
            </Box>
          </Box>
          <Typography
            variant="caption"
            sx={{ color: 'white', mt: 1, textTransform: 'capitalize' }}
          >
            {status === 'confirming' ? 'Processing...' : 'Uploading...'}
          </Typography>
        </Box>
      )}

      {/* Completed Check Animation */}
      {isCompleted && (
        <Fade in timeout={300}>
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(theme.palette.success.main, 0.3),
            }}
          >
            <CheckCircleIcon
              sx={{
                color: theme.palette.success.main,
                fontSize: 48,
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
              }}
            />
          </Box>
        </Fade>
      )}

      {/* Hover Actions Overlay */}
      <Fade in={showOverlay}>
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: alpha(theme.palette.common.black, 0.5),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
          }}
        >
          {showDragHandle && (
            <Tooltip title="Drag to reorder">
              <IconButton
                size="small"
                sx={{
                  color: 'white',
                  bgcolor: alpha(theme.palette.common.white, 0.15),
                  '&:hover': {
                    bgcolor: alpha(theme.palette.common.white, 0.25),
                  },
                }}
              >
                <DragIndicatorIcon />
              </IconButton>
            </Tooltip>
          )}
          {onPreview && (
            <Tooltip title="View fullscreen">
              <IconButton
                onClick={onPreview}
                size="small"
                sx={{
                  color: 'white',
                  bgcolor: alpha(theme.palette.common.white, 0.15),
                  '&:hover': {
                    bgcolor: alpha(theme.palette.common.white, 0.25),
                  },
                }}
              >
                <FullscreenIcon />
              </IconButton>
            </Tooltip>
          )}
          {onDelete && (
            <Tooltip title="Delete image">
              <IconButton
                onClick={onDelete}
                size="small"
                sx={{
                  color: 'white',
                  bgcolor: alpha(theme.palette.error.main, 0.8),
                  '&:hover': {
                    bgcolor: theme.palette.error.main,
                  },
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Fade>

      {/* Error Badge */}
      {hasError && (
        <Tooltip title={uploadingImage?.error || 'Upload failed'}>
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: theme.palette.error.main,
              borderRadius: '50%',
              p: 0.5,
              display: 'flex',
              boxShadow: theme.shadows[2],
            }}
          >
            <ErrorOutlineIcon sx={{ color: 'white', fontSize: 18 }} />
          </Box>
        </Tooltip>
      )}

      {/* Pending Moderation Badge */}
      {isPending && !isUploading && (
        <Tooltip title="Pending review">
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: theme.palette.warning.main,
              borderRadius: '50%',
              p: 0.5,
              display: 'flex',
              boxShadow: theme.shadows[2],
            }}
          >
            <HourglassEmptyIcon sx={{ color: 'white', fontSize: 18 }} />
          </Box>
        </Tooltip>
      )}

      {/* Rejected Badge */}
      {isRejected && !isUploading && (
        <Tooltip title={image?.rejection_reason || 'Image rejected'}>
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: theme.palette.error.main,
              borderRadius: '50%',
              p: 0.5,
              display: 'flex',
              boxShadow: theme.shadows[2],
            }}
          >
            <BlockIcon sx={{ color: 'white', fontSize: 18 }} />
          </Box>
        </Tooltip>
      )}
    </Card>
  );
};

export default ImageCard;
